import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Tag, Space, Select, message, Typography, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { orderApi } from '../services/api';
import { fmtCN } from '../utils/time';
import { navigateToOrderDetail } from '../components/MainLayout';

const { Text } = Typography;

const STATUS_FILTERS = [
  { value: 'all', label: '全部工单' },
  { value: 'awaiting_customer', label: '待客服处理工单' },
  { value: 'pending', label: '待跟进工单' },
  { value: 'processing', label: '处理中工单' },
  { value: 'completed', label: '已完成工单' },
];

const STATUS_COLOR = {
  pending: 'orange',
  processing: 'blue',
  awaiting_customer: 'purple',
  completed: 'green',
};

// 状态文案按工单类型区分（调度/申诉/索赔）
const STATUS_TEXT_BY_TYPE = {
  order: { pending: '调度待跟进', processing: '调度处理中', awaiting_customer: '待客服处理', completed: '调度完成' },
  appeal: { pending: '申诉待跟进', processing: '申诉处理中', awaiting_customer: '待客服处理', completed: '申诉完成' },
  claim: { pending: '索赔待跟进', processing: '索赔处理中', awaiting_customer: '待客服处理', completed: '索赔完成' },
};

const PRIORITY_TAG = {
  urgent: { color: 'red', text: '紧急重要' },
  high: { color: 'orange', text: '一般重要' },
  normal: { color: 'blue', text: '普通' },
  low: { color: 'default', text: '普通' },
};

const TYPE_TAG = {
  order: { color: 'geekblue', text: '调度工单' },
  appeal: { color: 'purple', text: '申诉工单' },
  claim: { color: 'magenta', text: '索赔工单' },
};

// 工单状态 → 各模块流转路由段（申诉/索赔按当前状态落到对应二级路由）
const STATUS_SEGMENT = {
  pending: 'pending',
  processing: 'processing',
  awaiting_customer: 'awaiting-customer',
  completed: 'completed',
};

export default function MyAllOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initStatus = STATUS_FILTERS.some((s) => s.value === searchParams.get('status'))
    ? searchParams.get('status') : 'all';
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState(initStatus);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, page, page_size: pageSize };
      if (keyword.trim()) params.keyword = keyword.trim();
      const res = await orderApi.listAllTypes(params);
      setData(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, pageSize, statusFilter]);

  const handleSearch = () => { setPage(1); fetchData(); };

  const handleRowClick = (record) => {
    if (record.type === 'order') {
      // 打开详情即标记已读：蓝点变灰（后端持久化 customer_unread）
      if (record.customer_unread) {
        setData((prev) => prev.map((x) => (x === record ? { ...x, customer_unread: false } : x)));
        orderApi.customerRead(record.id).catch(() => {});
      }
      navigateToOrderDetail(navigate, record.id);
    } else if (record.type === 'claim') {
      // 按当前状态落到索赔模块对应的流转二级路由（待跟进/处理中/待客服处理/完成）
      navigate(`/claims/${STATUS_SEGMENT[record.status] || 'pending'}?claim_id=${record.id}`);
    } else {
      navigate(`/appeals/${STATUS_SEGMENT[record.status] || 'pending'}?appeal_id=${record.id}`);
    }
  };

  const columns = [
    {
      title: '工单号',
      dataIndex: 'no',
      key: 'no',
      width: 160,
      render: (v, r) => (
        <Space direction="vertical" size={2}>
          <Text strong copyable>{v || '-'}</Text>
          <Tag color={TYPE_TAG[r.type]?.color} style={{ margin: 0, width: 'fit-content' }}>
            {TYPE_TAG[r.type]?.text}
          </Tag>
        </Space>
      ),
    },
    { title: '客户', dataIndex: 'customer_name', key: 'customer_name', width: 160, ellipsis: true, render: (v) => v || '-' },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (v) => v ? <Tag color={PRIORITY_TAG[v]?.color}>{PRIORITY_TAG[v]?.text || v}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v, r) => {
        const unread = r.customer_unread;
        const text = STATUS_TEXT_BY_TYPE[r.type]?.[v] || v;
        return (
          <Space size={6}>
            <Tooltip title={unread ? '待客服处理未读' : '已读'}>
              <span
                style={{
                  width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                  background: unread ? '#1677ff' : '#d9d9d9',
                }}
              />
            </Tooltip>
            <Tag color={STATUS_COLOR[v]}>{text}</Tag>
          </Space>
        );
      },
    },
    { title: '承运商/渠道', dataIndex: 'carrier', key: 'carrier', width: 140, ellipsis: true, render: (v) => v || '-' },
    {
      title: '问题摘要',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      render: (v) => <span style={{ color: '#555' }}>{v || '-'}</span>,
    },
    { title: '归属人', dataIndex: 'owner_name', key: 'owner_name', width: 90, render: (v) => v || '-' },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => v ? fmtCN(v) : '-',
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h2>查看我的所有工单</h2>
        <p className="desc">汇总您可见的调度工单、申诉工单与索赔工单（权限依据后台客户分配规则），可按状态筛选并选择每页显示数量。</p>
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索工单号 / 客户 / 承运商 / 问题摘要"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 320 }}
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            style={{ width: 170 }}
            options={STATUS_FILTERS}
          />
          <Button type="primary" onClick={handleSearch}>查询</Button>
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(r) => `${r.type}-${r.id}`}
          loading={loading}
          scroll={{ x: 1100 }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '200'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
}
